import { useEffect, useState, type FormEvent } from "react";
import { AlertCircle, ArrowRight, CheckCircle2, Eye, EyeOff, Loader2, Lock, User } from "lucide-react";
import { WolfLogo } from "../components/WolfLogo";
import { aceptarInvitacion, verInvitacion } from "../api";

const LARGO_MINIMO = 12;

export function InvitacionPage() {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [email, setEmail] = useState<string | null>(null);
  const [estado, setEstado] = useState<"cargando" | "invalida" | "formulario" | "lista">("cargando");
  const [nombre, setNombre] = useState("");
  const [clave, setClave] = useState("");
  const [repetir, setRepetir] = useState("");
  const [ver, setVer] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setEstado("invalida");
      return;
    }
    verInvitacion(token)
      .then((r) => {
        setEmail(r.email);
        setEstado("formulario");
      })
      .catch(() => setEstado("invalida"));
  }, [token]);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (clave.length < LARGO_MINIMO) return setError(`La contraseña debe tener al menos ${LARGO_MINIMO} caracteres.`);
    if (clave !== repetir) return setError("Las contraseñas no coinciden.");
    setEnviando(true);
    setError(null);
    try {
      await aceptarInvitacion(token, clave, nombre);
      setEstado("lista");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No fue posible crear la cuenta.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <WolfLogo height={32} />
          </div>
          <h1 className="login-title">Crea tu acceso</h1>
          <p className="login-subtitle">Portal de Clientes · Dashboard y Respuestas Sociales</p>
        </div>

        {estado === "cargando" && (
          <p role="status" className="login-subtitle">Verificando tu invitación…</p>
        )}

        {estado === "invalida" && (
          <div className="login-alert-error" role="alert">
            <AlertCircle size={18} style={{ flexShrink: 0, marginTop: "2px" }} />
            <span>Esta invitación no es válida o ya venció. Pide una nueva a quien te invitó.</span>
          </div>
        )}

        {estado === "lista" && (
          <>
            <p role="status" style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <CheckCircle2 size={18} aria-hidden="true" /> Tu cuenta quedó creada.
            </p>
            <a href="/login" className="login-submit-btn" style={{ textAlign: "center", textDecoration: "none" }}>
              <span>Iniciar sesión</span>
              <ArrowRight size={16} />
            </a>
          </>
        )}

        {estado === "formulario" && (
          <>
            {error && (
              <div className="login-alert-error" role="alert" style={{ marginBottom: "16px" }}>
                <AlertCircle size={18} style={{ flexShrink: 0, marginTop: "2px" }} />
                <span>{error}</span>
              </div>
            )}
            <form className="login-form" onSubmit={enviar}>
              <p className="login-subtitle">Cuenta para <strong>{email}</strong></p>
              <div className="login-field">
                <label htmlFor="inv-nombre">Tu nombre</label>
                <div className="login-input-wrapper">
                  <User size={16} className="login-input-icon" />
                  <input id="inv-nombre" className="login-input" value={nombre} onChange={(e) => setNombre(e.target.value)} autoComplete="name" maxLength={80} disabled={enviando} />
                </div>
              </div>
              <div className="login-field">
                <label htmlFor="inv-clave">Contraseña nueva</label>
                <div className="login-input-wrapper">
                  <Lock size={16} className="login-input-icon" />
                  <input
                    id="inv-clave"
                    type={ver ? "text" : "password"}
                    className="login-input"
                    value={clave}
                    onChange={(e) => setClave(e.target.value)}
                    autoComplete="new-password"
                    minLength={LARGO_MINIMO}
                    disabled={enviando}
                    required
                  />
                  <button type="button" className="login-input-toggle" onClick={() => setVer(!ver)} title={ver ? "Ocultar contraseña" : "Ver contraseña"} tabIndex={-1}>
                    {ver ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <small>Al menos {LARGO_MINIMO} caracteres.</small>
              </div>
              <div className="login-field">
                <label htmlFor="inv-repetir">Repite la contraseña</label>
                <div className="login-input-wrapper">
                  <Lock size={16} className="login-input-icon" />
                  <input id="inv-repetir" type={ver ? "text" : "password"} className="login-input" value={repetir} onChange={(e) => setRepetir(e.target.value)} autoComplete="new-password" disabled={enviando} required />
                </div>
              </div>
              <button type="submit" className="login-submit-btn" disabled={enviando}>
                {enviando ? (
                  <>
                    <Loader2 size={16} className="spin" />
                    <span>Creando cuenta…</span>
                  </>
                ) : (
                  <>
                    <span>Crear cuenta</span>
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
